import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { analyticsClient } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Feature flags fetched from the backend.
 */
interface FeaturesResponse {
  actionsEnabled?: boolean;
  rulesEnabled?: boolean;
  channelsEnabled?: boolean;
  outboundDeliveriesEnabled?: boolean;
  trinoEnabled: boolean;
  dashboardsEnabled: boolean;
  gisEnabled: boolean;
  entityModelsUiEnabled: boolean;
}

/**
 * Context type exposing feature flags and loading state.
 */
export interface FeaturesContextType {
  actionsEnabled?: boolean;
  rulesEnabled?: boolean;
  channelsEnabled?: boolean;
  outboundDeliveriesEnabled?: boolean;
  trinoEnabled: boolean;
  dashboardsEnabled: boolean;
  gisEnabled: boolean;
  entityModelsUiEnabled: boolean;
  loading: boolean;
  error: Error | null;
}

/**
 * Default context value - dashboards on by default, deferred surfaces off by default.
 * Rules (ADR-022 Phase 3) default OFF everywhere until the backend opts a tenant in.
 */
const defaultContext: FeaturesContextType = {
  actionsEnabled: false,
  rulesEnabled: false,
  channelsEnabled: false,
  outboundDeliveriesEnabled: false,
  trinoEnabled: false,
  dashboardsEnabled: true,
  gisEnabled: false,
  entityModelsUiEnabled: false,
  loading: true,
  error: null,
};

export const FeaturesContext = createContext<FeaturesContextType>(defaultContext);

interface FeaturesProviderProps {
  children: ReactNode;
}

/**
 * Provider component that fetches feature flags from the backend on mount.
 * Falls back to the launch-safe default state if the backend is unreachable.
 */
export const FeaturesProvider: React.FC<FeaturesProviderProps> = ({ children }) => {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [trinoEnabled, setTrinoEnabled] = useState<boolean>(false);
  const [actionsEnabled, setActionsEnabled] = useState<boolean>(false);
  const [rulesEnabled, setRulesEnabled] = useState<boolean>(false);
  const [channelsEnabled, setChannelsEnabled] = useState<boolean>(false);
  const [outboundDeliveriesEnabled, setOutboundDeliveriesEnabled] = useState<boolean>(false);
  const [dashboardsEnabled, setDashboardsEnabled] = useState<boolean>(true);
  const [gisEnabled, setGisEnabled] = useState<boolean>(false);
  const [entityModelsUiEnabled, setEntityModelsUiEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (authLoading) {
      setLoading(true);
      return () => {
        isMounted = false;
      };
    }

      if (!isAuthenticated) {
      if (isMounted) {
        setTrinoEnabled(false);
        setActionsEnabled(false);
        setRulesEnabled(false);
        setChannelsEnabled(false);
        setOutboundDeliveriesEnabled(false);
        setDashboardsEnabled(true);
        setGisEnabled(false);
        setEntityModelsUiEnabled(false);
        setError(null);
        setLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    const fetchFeatures = async () => {
      if (isMounted) {
        setLoading(true);
      }
      try {
        const response = await analyticsClient.get<FeaturesResponse>('/api/v1/features');
        if (isMounted) {
          setTrinoEnabled(response.data.trinoEnabled);
          setActionsEnabled(response.data.actionsEnabled === true);
          setRulesEnabled(response.data.rulesEnabled === true);
          setChannelsEnabled(response.data.channelsEnabled === true);
          setOutboundDeliveriesEnabled(response.data.outboundDeliveriesEnabled === true);
          setDashboardsEnabled(response.data.dashboardsEnabled ?? true);
          setGisEnabled(response.data.gisEnabled ?? false);
          setEntityModelsUiEnabled(response.data.entityModelsUiEnabled ?? false);
          setError(null);
        }
      } catch (err) {
        console.warn('Failed to fetch features from backend, defaulting to launch-safe feature defaults:', err);
        if (isMounted) {
          // Graceful degradation: keep dashboards available, keep deferred surfaces hidden.
          setTrinoEnabled(false);
          setActionsEnabled(false);
          setRulesEnabled(false);
          setChannelsEnabled(false);
          setOutboundDeliveriesEnabled(false);
          setDashboardsEnabled(true);
          setGisEnabled(false);
          setEntityModelsUiEnabled(false);
          setError(err instanceof Error ? err : new Error('Failed to fetch features'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchFeatures();

    return () => {
      isMounted = false;
    };
  }, [authLoading, isAuthenticated]);

  return (
    <FeaturesContext.Provider
      value={{ actionsEnabled, rulesEnabled, channelsEnabled, outboundDeliveriesEnabled, trinoEnabled, dashboardsEnabled, gisEnabled, entityModelsUiEnabled, loading, error }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
